import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import TimeRangePicker from "../../../components/ui/TimeRangePicker";
import RollingDayPresets from "../../../features/filter/TabFilterModal/RollingDayPresets";
import TooltipAttributeSettingsField from "../../../components/common/TooltipAttributeSettingsField";
import { useOcelMetadata } from "../../../stores/OcelContext";

/** Format a date string as "DD. Monat YYYY" (e.g., 06. Februar 2025). */
function formatDisplayDate(value) {
  const parsed = new Date(value + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

const parseNonNegativeIntegerInput = (value, max = null) => {
  if (value === "") return "";
  if (!/^\d+$/.test(value)) return "";

  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return "";

  const normalized = Math.max(0, parsed);
  return max == null ? normalized : Math.min(max, normalized);
};

const ResourceStatusMonitorSettings = ({
  title,
  objectTypes,
  selectedObjectType,
  availableResources,
  configuredResources,
  shiftStart,
  shiftEnd,
  breaks,
  toleranceThreshold,
  ignoreShortTransitions,
  includeNoDataResources,
  multiResourceMode,
  downtimeRedThreshold,
  blockSizeMinutes,
  showShiftOnly,
  tooltipObjectType,
  tooltipAttributes,
  activeFile,
  selectedDate,
  rollingOffset,
  resourceLastActiveDate,
  onTitleChange,
  onObjectTypeChange,
  onConfiguredResourcesChange,
  onShiftStartChange,
  onShiftEndChange,
  onAddBreak,
  onRemoveBreak,
  onUpdateBreak,
  onToleranceThresholdChange,
  onIgnoreShortTransitionsChange,
  onIncludeNoDataResourcesChange,
  onMultiResourceModeChange,
  onDowntimeRedThresholdChange,
  onBlockSizeMinutesChange,
  onShowShiftOnlyChange,
  onTooltipObjectTypeChange,
  onTooltipAttributesChange,
  onSelectedDateChange,
  loading = false,
}) => {
  const { t } = useTranslation("tiles");
  // Tooltip object type — deliberately independent of the tile's own
  // Object Type field above: that field plays no role in the resource/
  // downtime computation itself (only used for a cache key), so it may not
  // be the type actually linked to resource-tagged events. Empty means
  // "fall back to the tile's Object Type" (kept as the default for
  // convenience, but overridable here).
  const effectiveTooltipObjectType = tooltipObjectType || selectedObjectType;

  // Tooltip attribute options — object attributes of the effective tooltip
  // object type, for the click-triggered extended tooltip (analog zur
  // Tooltip-Konfiguration bei Aggregation KPI / Gantt Chart).
  const metadata = useOcelMetadata(activeFile);
  const tooltipAttributeOptions = useMemo(() => {
    const allObjectAttributes = metadata?.object_attribute_names || [];
    if (!effectiveTooltipObjectType) return allObjectAttributes;
    const scoped = (metadata?.object_types_schema || []).find((t) => t.name === effectiveTooltipObjectType);
    const names = (scoped?.attributes || []).map((a) => a.name);
    return names.length > 0 ? names : allObjectAttributes;
  }, [effectiveTooltipObjectType, metadata]);

  // Stale tooltip columns from a different object type shouldn't silently
  // linger (mirrors AggregationKPISettings.jsx's handleObjectTypeChange).
  const handleObjectTypeChange = (val) => {
    onObjectTypeChange(val);
    onTooltipAttributesChange([]);
  };

  const handleTooltipObjectTypeChange = (val) => {
    onTooltipObjectTypeChange(val);
    onTooltipAttributesChange([]);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Resource Status Monitor"
          disabled={loading}
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Object Type and Resources
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Object Type *
            </label>
            <select
              value={selectedObjectType}
              onChange={(e) => handleObjectTypeChange(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={loading}
            >
              <option value="">Select Object Type</option>
              {(objectTypes || []).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <MultiSelectDropdown
              label="Configured Resources *"
              options={availableResources.map(String)}
              selectedValues={configuredResources}
              onSelectionChange={onConfiguredResourcesChange}
              placeholder={
                availableResources.length === 0
                  ? "Load data first..."
                  : "Select resources..."
              }
              disabled={loading || availableResources.length === 0}
              itemType="resources"
              explicitDisplayCount={5}
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <TooltipAttributeSettingsField
          attributeOptions={tooltipAttributeOptions}
          selectedAttributes={tooltipAttributes}
          onSelectedAttributesChange={onTooltipAttributesChange}
          trigger="hoverOrClick"
          disabled={loading}
          showTopDivider={false}
          objectTypeOverride={{
            value: tooltipObjectType,
            options: objectTypes || [],
            onChange: handleTooltipObjectTypeChange,
            baseType: selectedObjectType,
            hint: t("tooltipConfig.objectTypeHint"),
          }}
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Date
        </label>
        <p className="text-xs text-gray-500">
          Currently selected: {formatDisplayDate(selectedDate)}
        </p>
        <RollingDayPresets value={rollingOffset} onChange={onSelectedDateChange} lastActiveDate={resourceLastActiveDate} />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <TimeRangePicker
          startTime={shiftStart}
          endTime={shiftEnd}
          onStartChange={onShiftStartChange}
          onEndChange={onShiftEndChange}
          label="Shift Schedule *"
          hint="Shift is reused from Total Downtime logic."
          disabled={loading}
        />
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showShiftOnly}
            onChange={(e) => onShowShiftOnlyChange(e.target.checked)}
            className="rounded border-gray-300 focus:ring-primary-500"
            style={{ accentColor: "#0066cc" }}
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Show shift window only
          </span>
        </label>
        {showShiftOnly && (
          <p className="text-xs text-gray-500 ml-6">
            The timeline starts at {shiftStart} and ends at {shiftEnd}. Outside-shift slots are hidden.
          </p>
        )}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Breaks
          </label>
          <button
            onClick={onAddBreak}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100"
            disabled={loading}
          >
            Add Break
          </button>
        </div>
        {breaks.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No breaks configured.</p>
        ) : (
          <div className="space-y-2">
            {breaks.map((brk, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-md p-2"
              >
                <TimeRangePicker
                  startTime={brk.start}
                  endTime={brk.end}
                  onStartChange={(val) => onUpdateBreak(idx, "start", val)}
                  onEndChange={(val) => onUpdateBreak(idx, "end", val)}
                  startLabel="From"
                  endLabel="To"
                  disabled={loading}
                  className="flex-1"
                />
                <button
                  onClick={() => onRemoveBreak(idx)}
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                  title="Remove break"
                  disabled={loading}
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Multi-Resource Mode
        </label>
        <select
          value={multiResourceMode}
          onChange={(e) => onMultiResourceModeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={loading}
        >
          <option value="accumulated">Accumulated</option>
          <option value="concurrent">Concurrent</option>
        </select>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Downtime threshold for red slots (minutes)
        </label>
        <p className="text-xs text-gray-500">
          Slots with downtime &ge; this value are shown in red. Slots with less
          downtime are shown in orange. Set to 0 to always show red.
        </p>
        <input
          type="number"
          value={downtimeRedThreshold}
          onChange={(e) =>
            onDowntimeRedThresholdChange(
              parseNonNegativeIntegerInput(e.target.value),
            )
          }
          min="0"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={loading}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {t("resourceStatus.blockSize.label")}
        </label>
        <select
          value={blockSizeMinutes}
          onChange={(e) => onBlockSizeMinutesChange(Number(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={loading}
        >
          {[5, 10, 15, 20].map((minutes) => (
            <option key={minutes} value={minutes}>
              {t("resourceStatus.blockSize.option", { count: minutes })}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={ignoreShortTransitions}
            onChange={(e) => onIgnoreShortTransitionsChange(e.target.checked)}
            className="rounded border-gray-300 focus:ring-primary-500"
            style={{ accentColor: "#0066cc" }}
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Ignore short transitions
          </span>
        </label>
      </div>

      {ignoreShortTransitions && (
        <div className="ml-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transition tolerance (seconds)
          </label>
          <input
            type="number"
            value={toleranceThreshold}
            onChange={(e) =>
              onToleranceThresholdChange(
                parseNonNegativeIntegerInput(e.target.value),
              )
            }
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={loading}
          />
        </div>
      )}

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeNoDataResources}
            onChange={(e) => onIncludeNoDataResourcesChange(e.target.checked)}
            className="rounded border-gray-300 focus:ring-primary-500"
            style={{ accentColor: "#0066cc" }}
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Count resources without data as full downtime
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          When enabled, resources with no records in the dataset are treated as
          100% down for every analyzed day
        </p>
      </div>
    </div>
  );
};

export default ResourceStatusMonitorSettings;
